import { useState } from 'react';
import { ExternalLink, Loader2, AlertTriangle } from 'lucide-react';
import { useWorkspace, TIER_LIMITS, type SubscriptionTier } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/AuthContext';
import { useProducts } from '../../contexts/ProductContext';
import { useMachines } from '../../contexts/MachineContext';
import { useFonts } from '../../contexts/FontContext';

const TIER_LABELS: Record<SubscriptionTier, string> = {
  free: 'Free',
  starter: 'Starter',
  pro: 'Pro',
  business: 'Business',
  admin: 'Admin',
};

const TIER_COLORS: Record<SubscriptionTier, string> = {
  free: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  starter: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  pro: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  business: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  admin: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

// Tiers shown in the plan comparison table (excludes admin)
const VISIBLE_TIERS: SubscriptionTier[] = ['free', 'starter', 'pro', 'business'];

const TIER_ORDER: SubscriptionTier[] = ['free', 'starter', 'pro', 'business'];

function UsageBar({ label, current, limit }: { label: string; current: number; limit: number }) {
  const isUnlimited = limit === Infinity;
  const percent = isUnlimited ? 0 : Math.min((current / limit) * 100, 100);
  const isNearLimit = !isUnlimited && percent >= 80;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600 dark:text-gray-400">{label}</span>
        <span className={`font-medium ${isNearLimit ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>
          {current} / {isUnlimited ? 'Unlimited' : limit}
        </span>
      </div>
      {!isUnlimited && (
        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              isNearLimit ? 'bg-amber-500' : 'bg-blue-500'
            }`}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
    </div>
  );
}

export default function SubscriptionTab() {
  const { tier, tierLimits, usage, workspace, refreshWorkspace } = useWorkspace();
  const { customerPortal, checkout, changePlan } = useAuth();
  const { products } = useProducts();
  const { machines } = useMachines();
  const { fonts } = useFonts();
  const [changingTo, setChangingTo] = useState<string | null>(null);

  const isPaid = tier !== 'free' && tier !== 'admin';
  const cancelAt = workspace?.cancelAt;
  const isCanceling = cancelAt != null;
  const currentTierIdx = TIER_ORDER.indexOf(tier);

  async function handleChangePlan(targetTier: SubscriptionTier) {
    setChangingTo(targetTier);
    try {
      const success = await changePlan(targetTier);
      if (success) {
        // Give webhook a moment to update Supabase, then refresh
        await new Promise(r => setTimeout(r, 1500));
        await refreshWorkspace();
      }
    } finally {
      setChangingTo(null);
    }
  }

  function getPlanButtonProps(t: SubscriptionTier) {
    if (t === 'free' || t === tier) return null;
    const targetIdx = TIER_ORDER.indexOf(t);
    const isUpgrade = targetIdx > currentTierIdx;
    return {
      label: isUpgrade ? `Upgrade to ${TIER_LABELS[t]}` : `Switch to ${TIER_LABELS[t]}`,
      className: isUpgrade
        ? 'bg-blue-600 hover:bg-blue-700 text-white'
        : 'bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300',
    };
  }

  return (
    <div className="space-y-8">
      {/* Current Plan */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Current Plan</h3>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${TIER_COLORS[tier]}`}>
                {TIER_LABELS[tier]}
              </span>
              {tier === 'free' && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Upgrade to unlock more features
                </span>
              )}
            </div>
            {isPaid && (
              <button
                onClick={customerPortal}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition"
              >
                Manage Subscription
                <ExternalLink size={14} />
              </button>
            )}
          </div>

          {/* Cancellation notice */}
          {isCanceling && (
            <div className="mt-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
              <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="text-sm">
                <p className="font-medium text-amber-800 dark:text-amber-300">
                  Your {TIER_LABELS[tier]} plan is scheduled to cancel on{' '}
                  {new Date(cancelAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
                <p className="mt-1 text-amber-700 dark:text-amber-400">
                  You'll keep full access to your current plan until then. After that, your account will switch to the Free plan.
                </p>
                <button
                  onClick={customerPortal}
                  className="mt-2 text-amber-800 dark:text-amber-300 font-medium underline hover:no-underline"
                >
                  Reactivate subscription
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Usage */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Usage</h3>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 space-y-4">
          <UsageBar label="Products" current={products.length} limit={tierLimits.products} />
          <UsageBar label="Machines" current={machines.length} limit={tierLimits.machines} />
          <UsageBar label="Fonts" current={fonts.length} limit={tierLimits.fonts} />
          <UsageBar
            label={`Downloads (${usage?.period || 'this month'})`}
            current={usage?.downloadCount || 0}
            limit={tierLimits.downloadsPerMonth}
          />
        </div>
      </div>

      {/* Plan Comparison */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Plan Comparison</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800">
                <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Feature</th>
                {VISIBLE_TIERS.map(t => (
                  <th
                    key={t}
                    className={`px-4 py-3 text-center font-medium ${
                      t === tier ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {TIER_LABELS[t]}
                    {t === tier && ' (current)'}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {[
                { label: 'Products', key: 'products' as const },
                { label: 'Machines', key: 'machines' as const },
                { label: 'Fonts', key: 'fonts' as const },
                { label: 'Downloads / mo', key: 'downloadsPerMonth' as const },
              ].map(row => (
                <tr key={row.key} className="bg-white dark:bg-gray-900">
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">{row.label}</td>
                  {VISIBLE_TIERS.map(t => (
                    <td
                      key={t}
                      className={`px-4 py-3 text-center ${
                        t === tier ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {TIER_LIMITS[t][row.key] === Infinity ? 'Unlimited' : TIER_LIMITS[t][row.key]}
                    </td>
                  ))}
                </tr>
              ))}
              {/* Action buttons row — shown for free and paid users (not admin) */}
              {tier !== 'admin' && (
                <tr className="bg-white dark:bg-gray-900">
                  <td className="px-4 py-3"></td>
                  {VISIBLE_TIERS.map(t => (
                    <td key={t} className="px-4 py-3 text-center">
                      {t === tier ? (
                        <span className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                          Current Plan
                        </span>
                      ) : t === 'free' ? (
                        // No button for free column (use Manage Subscription to cancel)
                        null
                      ) : tier === 'free' ? (
                        // Free user: checkout for new subscription
                        <button
                          onClick={() => checkout(t)}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition"
                        >
                          Choose {TIER_LABELS[t]}
                        </button>
                      ) : (
                        // Paid user: change plan
                        (() => {
                          const props = getPlanButtonProps(t);
                          if (!props) return null;
                          const isChanging = changingTo === t;
                          return (
                            <button
                              onClick={() => handleChangePlan(t)}
                              disabled={changingTo !== null}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition inline-flex items-center gap-1.5 disabled:opacity-50 ${props.className}`}
                            >
                              {isChanging && <Loader2 size={12} className="animate-spin" />}
                              {isChanging ? 'Changing...' : props.label}
                            </button>
                          );
                        })()
                      )}
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
